import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../context/API';

// Types
export interface ShiftDetails {
  start_time: string;
  end_time: string;
  section: string;
  specials: string;
}

export interface DailyTask {
  description: string;
}

export interface PerformanceMetrics {
  serviceSpeed: string;
  customerFeedback: string;
  salesSummary: string;
}

export interface Announcement {
  message: string;
}

interface StaffDashboardState {
  shiftDetails: ShiftDetails | null;
  dailyTasks: DailyTask[];
  performanceMetrics: PerformanceMetrics | null;
  announcements: Announcement[];
  loading: {
    shiftDetails: boolean;
    dailyTasks: boolean;
    performanceMetrics: boolean;
    announcements: boolean;
  };
  error: string | null;
}

const initialState: StaffDashboardState = {
  shiftDetails: null,
  dailyTasks: [],
  performanceMetrics: null,
  announcements: [],
  loading: {
    shiftDetails: false,
    dailyTasks: false,
    performanceMetrics: false,
    announcements: false,
  },
  error: null,
};

// Async Thunks
export const fetchShiftDetails = createAsyncThunk<
  ShiftDetails,
  void,
  { rejectValue: string }
>('staffDashboard/fetchShiftDetails', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get<{ data: ShiftDetails } | ShiftDetails>('staff/shift', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    const data = response.data;
    if ('data' in data && data.data) {
      return data.data as ShiftDetails;
    }
    return data as ShiftDetails;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch shift details'
    );
  }
});

export const fetchDailyTasks = createAsyncThunk<
  DailyTask[],
  void,
  { rejectValue: string }
>('staffDashboard/fetchDailyTasks', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get<{ data: DailyTask[] }>('staff/tasks', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data?.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch daily tasks'
    );
  }
});

export const fetchPerformanceMetrics = createAsyncThunk<
  PerformanceMetrics,
  void,
  { rejectValue: string }
>('staffDashboard/fetchPerformanceMetrics', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get<{ data: PerformanceMetrics } | PerformanceMetrics>('staff/performance', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    const data = response.data;
    if ('data' in data && data.data) {
      return data.data as PerformanceMetrics;
    }
    return data as PerformanceMetrics;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch performance metrics'
    );
  }
});

export const fetchAnnouncements = createAsyncThunk<
  Announcement[],
  void,
  { rejectValue: string }
>('staffDashboard/fetchAnnouncements', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get<{ data: Announcement[] }>('announcements', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data?.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch announcements'
    );
  }
});

// Slice
const staffDashboardSlice = createSlice({
  name: 'staffDashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch shift details
      .addCase(fetchShiftDetails.pending, (state) => {
        state.loading.shiftDetails = true;
        state.error = null;
      })
      .addCase(fetchShiftDetails.fulfilled, (state, action) => {
        state.loading.shiftDetails = false;
        state.shiftDetails = action.payload;
      })
      .addCase(fetchShiftDetails.rejected, (state, action) => {
        state.loading.shiftDetails = false;
        state.error = action.payload as string;
      })
      // Fetch daily tasks
      .addCase(fetchDailyTasks.pending, (state) => {
        state.loading.dailyTasks = true;
        state.error = null;
      })
      .addCase(fetchDailyTasks.fulfilled, (state, action) => {
        state.loading.dailyTasks = false;
        state.dailyTasks = action.payload;
      })
      .addCase(fetchDailyTasks.rejected, (state, action) => {
        state.loading.dailyTasks = false;
        state.error = action.payload as string;
      })
      // Fetch performance metrics
      .addCase(fetchPerformanceMetrics.pending, (state) => {
        state.loading.performanceMetrics = true;
        state.error = null;
      })
      .addCase(fetchPerformanceMetrics.fulfilled, (state, action) => {
        state.loading.performanceMetrics = false;
        state.performanceMetrics = action.payload;
      })
      .addCase(fetchPerformanceMetrics.rejected, (state, action) => {
        state.loading.performanceMetrics = false;
        state.error = action.payload as string;
      })
      // Fetch announcements
      .addCase(fetchAnnouncements.pending, (state) => {
        state.loading.announcements = true;
        state.error = null;
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.loading.announcements = false;
        state.announcements = action.payload;
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.loading.announcements = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = staffDashboardSlice.actions;
export default staffDashboardSlice.reducer;
